import { useState, useRef, useEffect } from "react";
import { Pencil, Eraser, RotateCcw, Save, Palette, Printer, Circle, Square, Triangle, Star, Heart, Hexagon, Check, Loader } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";

const COLORS = ["#073B4C", "#118AB2", "#06D6A0", "#FFD166", "#EF476F", "#9B5DE5", "#F77F00", "#FFFFFF"];

// Shape definitions
const SHAPES = [
  { id: "circle", icon: Circle, nameKey: "circle" },
  { id: "square", icon: Square, nameKey: "square" },
  { id: "triangle", icon: Triangle, nameKey: "triangle" },
  { id: "star", icon: Star, nameKey: "star_shape" },
  { id: "heart", icon: Heart, nameKey: "heart_shape" },
  { id: "hexagon", icon: Hexagon, nameKey: "hexagon" },
];

export default function DrawingCanvas({ 
  onSave, 
  savedDrawing, 
  title,
  height = 200,
  lessonId,
  childId,
  activityType = "general"
}) {
  const { t } = useLanguage();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [color, setColor] = useState("#073B4C");
  const [brushSize, setBrushSize] = useState(4);
  const [tool, setTool] = useState("pen"); // pen, eraser, or shape name
  const [hasDrawn, setHasDrawn] = useState(false);
  const [selectedShape, setSelectedShape] = useState(null);
  const [shapeSize, setShapeSize] = useState(40);
  const [showShapes, setShowShapes] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Load saved drawing if exists
    if (savedDrawing) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasDrawn(true);
      };
      img.src = savedDrawing;
    }
  }, [savedDrawing]);

  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return {
      x: x * (canvas.width / rect.width),
      y: y * (canvas.height / rect.height)
    };
  };

  const drawShape = (ctx, shape, x, y, size, fillColor) => {
    ctx.fillStyle = fillColor;
    ctx.strokeStyle = fillColor;
    ctx.lineWidth = 2;
    
    switch (shape) {
      case "circle":
        ctx.beginPath();
        ctx.arc(x, y, size / 2, 0, Math.PI * 2);
        ctx.fill();
        break;
        
      case "square":
        ctx.fillRect(x - size / 2, y - size / 2, size, size);
        break;
        
      case "triangle":
        ctx.beginPath();
        ctx.moveTo(x, y - size / 2);
        ctx.lineTo(x + size / 2, y + size / 2);
        ctx.lineTo(x - size / 2, y + size / 2);
        ctx.closePath();
        ctx.fill();
        break;
        
      case "star":
        drawStar(ctx, x, y, 5, size / 2, size / 4);
        ctx.fill();
        break;
        
      case "heart":
        drawHeart(ctx, x, y, size);
        ctx.fill();
        break;
        
      case "hexagon":
        drawPolygon(ctx, x, y, size / 2, 6);
        ctx.fill();
        break;
        
      default:
        break;
    }
  };

  const drawStar = (ctx, cx, cy, spikes, outerRadius, innerRadius) => {
    let rot = Math.PI / 2 * 3;
    let x = cx;
    let y = cy;
    let step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);
    
    for (let i = 0; i < spikes; i++) {
      x = cx + Math.cos(rot) * outerRadius;
      y = cy + Math.sin(rot) * outerRadius;
      ctx.lineTo(x, y);
      rot += step;

      x = cx + Math.cos(rot) * innerRadius;
      y = cy + Math.sin(rot) * innerRadius;
      ctx.lineTo(x, y);
      rot += step;
    }
    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
  };

  const drawHeart = (ctx, x, y, size) => {
    const topCurveHeight = size * 0.3;
    ctx.beginPath();
    ctx.moveTo(x, y + size / 4);
    
    // Left curve
    ctx.bezierCurveTo(
      x, y, 
      x - size / 2, y, 
      x - size / 2, y + topCurveHeight
    );
    
    // Left bottom
    ctx.bezierCurveTo(
      x - size / 2, y + (size + topCurveHeight) / 2, 
      x, y + (size + topCurveHeight) / 2, 
      x, y + size / 1.2
    );
    
    // Right bottom  
    ctx.bezierCurveTo(
      x, y + (size + topCurveHeight) / 2, 
      x + size / 2, y + (size + topCurveHeight) / 2, 
      x + size / 2, y + topCurveHeight
    );
    
    // Right curve
    ctx.bezierCurveTo(
      x + size / 2, y, 
      x, y, 
      x, y + size / 4
    );
    ctx.closePath();
  };

  const drawPolygon = (ctx, x, y, radius, sides) => {
    ctx.beginPath();
    ctx.moveTo(x + radius * Math.cos(0), y + radius * Math.sin(0));
    for (let i = 1; i <= sides; i++) {
      ctx.lineTo(
        x + radius * Math.cos(i * 2 * Math.PI / sides),
        y + radius * Math.sin(i * 2 * Math.PI / sides)
      );
    }
    ctx.closePath();
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoords(e);
    
    if (selectedShape) {
      // Draw shape at clicked position
      drawShape(ctx, selectedShape, x, y, shapeSize, color);
      setHasDrawn(true);
      return;
    }
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawing || selectedShape) return;
    e.preventDefault();
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { x, y } = getCanvasCoords(e);
    
    ctx.lineWidth = brushSize;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = tool === "eraser" ? "#FFFFFF" : color;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    setIsSaved(false);
  };

  const saveDrawing = async () => {
    setIsSaving(true);
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    try {
      await onSave?.(dataUrl, activityType);
      setIsSaved(true);
      // Reset saved state after 3 seconds
      setTimeout(() => setIsSaved(false), 3000);
    } catch (e) {
      console.error("Save failed:", e);
    }
    setIsSaving(false);
  };

  const printDrawing = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    
    // Create print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>My Bible Buds Drawing</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
              font-family: Arial, sans-serif;
            }
            h1 {
              color: #9B5DE5;
              margin-bottom: 10px;
              font-size: 24px;
            }
            .subtitle {
              color: #666;
              margin-bottom: 20px;
              font-size: 14px;
            }
            img {
              max-width: 100%;
              border: 3px solid #9B5DE5;
              border-radius: 15px;
            }
            .footer {
              margin-top: 20px;
              color: #999;
              font-size: 12px;
            }
            @media print {
              body { padding: 10px; }
              h1 { font-size: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>My Bible Buds Drawing</h1>
          <p class="subtitle">${title}</p>
          <img src="${dataUrl}" alt="Drawing" />
          <p class="footer">Created with Bible Buds - ${new Date().toLocaleDateString()}</p>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const selectTool = (toolName) => {
    setTool(toolName);
    setSelectedShape(null);
    setShowShapes(false);
  };

  const selectShape = (shapeId) => {
    setSelectedShape(shapeId);
    setTool("shape");
    setShowShapes(false);
  };

  return (
    <div className="bg-white rounded-3xl shadow-lg overflow-hidden mb-5">
      <div className="p-4 border-b" style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pencil size={20} color="white" strokeWidth={2.5} />
            <h3 className="font-black text-white" style={{ fontFamily: "Fredoka, sans-serif" }}>
              {title}
            </h3>
          </div>
          {/* Print button in header */}
          <button
            onClick={printDrawing}
            disabled={!hasDrawn}
            className="flex items-center gap-1 px-3 py-1 rounded-lg bg-white/20 text-white text-sm font-bold hover:bg-white/30 transition-all disabled:opacity-50"
            data-testid="print-drawing-btn"
          >
            <Printer size={16} /> Print
          </button>
        </div>
      </div>
      
      <div className="p-4">
        {/* Tools Row */}
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
          {/* Pen tool */}
          <button
            onClick={() => selectTool("pen")}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-bold transition-all ${
              tool === "pen" && !selectedShape 
                ? "bg-[#9B5DE5] text-white" 
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            data-testid="pen-tool-btn"
          >
            <Pencil size={16} /> Draw
          </button>
          
          {/* Eraser tool */}
          <button
            onClick={() => selectTool("eraser")}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-bold transition-all ${
              tool === "eraser" 
                ? "bg-gray-700 text-white" 
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            data-testid="eraser-tool-btn"
          >
            <Eraser size={16} /> {t('eraser') || 'Erase'}
          </button>
          
          {/* Shapes dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowShapes(!showShapes)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm font-bold transition-all ${
                selectedShape 
                  ? "bg-[#EF476F] text-white" 
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
              data-testid="shapes-dropdown-btn"
            >
              {selectedShape ? (
                SHAPES.find(s => s.id === selectedShape)?.icon && 
                (() => {
                  const ShapeIcon = SHAPES.find(s => s.id === selectedShape).icon;
                  return <ShapeIcon size={16} />;
                })()
              ) : (
                <Circle size={16} />
              )}
              {t('shapes')}
            </button>
            
            {/* Shapes dropdown menu */}
            {showShapes && (
              <div className="absolute top-full left-0 mt-1 bg-white rounded-2xl shadow-xl border p-2 z-10 min-w-[180px]">
                <p className="text-xs font-bold text-gray-400 px-2 mb-2">{t('select_shape') || 'SELECT A SHAPE'}</p>
                <div className="grid grid-cols-3 gap-1">
                  {SHAPES.map(({ id, icon: Icon, nameKey }) => (
                    <button
                      key={id}
                      onClick={() => selectShape(id)}
                      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                        selectedShape === id 
                          ? "bg-[#EF476F] text-white" 
                          : "hover:bg-gray-100 text-gray-600"
                      }`}
                      data-testid={`shape-${id}-btn`}
                    >
                      <Icon size={24} />
                      <span className="text-xs font-bold">{t(nameKey)}</span>
                    </button>
                  ))}
                </div>
                
                {/* Shape size */}
                <div className="mt-2 pt-2 border-t">
                  <p className="text-xs font-bold text-gray-400 px-2 mb-1">SIZE</p>
                  <div className="flex items-center gap-1 px-2">
                    {[25, 40, 60, 80].map((size) => (
                      <button
                        key={size}
                        onClick={() => setShapeSize(size)}
                        className={`flex-1 py-1 rounded-lg text-xs font-bold transition-all ${
                          shapeSize === size 
                            ? "bg-[#9B5DE5] text-white" 
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {size === 25 ? "S" : size === 40 ? "M" : size === 60 ? "L" : "XL"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Color palette */}
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Palette size={16} className="text-gray-400" />
          {COLORS.map((c) => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${
                color === c ? "border-gray-800 scale-110" : "border-gray-300"
              } ${c === "#FFFFFF" ? "bg-white" : ""}`}
              style={{ background: c }}
              data-testid={`color-${c}`}
            />
          ))}
        </div>

        {/* Brush size (only for pen/eraser) */}
        {(tool === "pen" || tool === "eraser") && !selectedShape && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-gray-500 font-bold">Brush:</span>
            {[2, 4, 8, 12].map((size) => (
              <button
                key={size}
                onClick={() => setBrushSize(size)}
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-all ${
                  brushSize === size ? "bg-gray-200" : "hover:bg-gray-100"
                }`}
              >
                <div 
                  className="rounded-full bg-gray-700" 
                  style={{ width: size + 2, height: size + 2 }}
                />
              </button>
            ))}
          </div>
        )}

        {/* Shape mode indicator */}
        {selectedShape && (
          <div className="flex items-center gap-2 mb-3 p-2 rounded-xl bg-[#EF476F]/10">
            <span className="text-xs font-bold text-[#EF476F]">
              Tap anywhere to place a {SHAPES.find(s => s.id === selectedShape)?.name}!
            </span>
          </div>
        )}

        {/* Canvas */}
        <div className="border-2 border-dashed border-gray-300 rounded-2xl overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            width={400}
            height={height}
            className="w-full touch-none"
            style={{ 
              height: `${height}px`,
              cursor: selectedShape ? "crosshair" : tool === "eraser" ? "cell" : "crosshair"
            }}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            data-testid="drawing-canvas"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={clearCanvas}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gray-100 text-gray-600 font-bold text-sm hover:bg-gray-200 transition-all"
            data-testid="clear-drawing-btn"
          >
            <RotateCcw size={16} /> {t('clear')}
          </button>
          <button
            onClick={printDrawing}
            disabled={!hasDrawn}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#118AB2] text-white font-bold text-sm hover:bg-[#0d7a9e] transition-all disabled:opacity-50"
            data-testid="print-drawing-btn-bottom"
          >
            <Printer size={16} /> {t('print')}
          </button>
          <button
            onClick={saveDrawing}
            disabled={!hasDrawn || isSaving}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-white font-bold text-sm disabled:opacity-50 transition-all ${
              isSaved ? 'bg-[#06D6A0]' : ''
            }`}
            style={!isSaved ? { background: "linear-gradient(90deg, #06D6A0, #118AB2)" } : {}}
            data-testid="save-drawing-btn"
          >
            {isSaving ? (
              <><Loader size={16} className="animate-spin" /> {t('saving') || 'Saving...'}</>
            ) : isSaved ? (
              <><Check size={16} /> {t('saved') || 'Saved!'}</>
            ) : (
              <><Save size={16} /> {t('save')}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
